import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/lib/table';
import { CheckOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import Table from '~/components/Table';
import CountryField from '~/components/fields/CountryField';
import { useGetCardholders } from '~/controllers/cardholder';
import colors from '~/constants/colors';
import { useGetApplication } from '~/controllers/wizard';
import Section from '~/layouts/Section ';

type RowDataType = {
  fullName: string;
  country: string;
};

interface CardSection {
  id: string;
  title: string;
  data: any[];
}

const cardRoleSections: { role: string; id: string; title: string }[] = [
  { role: 'cardholder', id: 'reviewAndSubmitCardholderTable', title: 'Corporate Executive Card' },
  { role: 'businessCardholder', id: 'reviewAndSubmitBusinessCardholderTable', title: 'Max Business Card' },
];

const pagination = { hideOnSinglePage: true };

const CardholdersTab: FC = () => {
  const { t } = useTranslation('fillApplication');
  const { data: payload } = useGetCardholders();
  const { data: applicationPayload } = useGetApplication();
  const {
    hierarchyLists: {
      DIGITAL_BUSINESS_PAYMENT: digitalBusinessPaymentUsers = [],
      DIGITAL_TRAVEL_PAYMENT: digitalTravelPaymentUsers = [],
    },
  } = applicationPayload?.payload?.cardOrderDetails;

  const columns = useMemo<ColumnsType<RowDataType>>(
    () => [
      {
        title: t('Full name'),
        dataIndex: 'fullName',
        key: 'fullName',
        width: '20%',
      },
      {
        title: t('Designtation'),
        dataIndex: 'designtation',
        key: 'designtation',
        width: '20%',
        render: (country) => <CountryField value={country} />,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: '20%',
      },
      {
        title: 'Phone number',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: '20%',
      },
      {
        title: 'Consent',
        dataIndex: 'consent',
        key: 'consent',
        width: '20%',
      },
    ],
    [t],
  );

  const columnsPortalUsers = useMemo<ColumnsType<RowDataType>>(
    () => [
      {
        title: t('Name'),
        dataIndex: 'name',
        key: 'name',
        width: '100%%',
      },
    ],
    [t],
  );

  if (!payload) return null;

  const cardSections = getCardSections(payload);

  function getVirtualCardUsers(data: any): readonly RowDataType[] | undefined {
    const users = data.filter((data: any) => data.type === 'cardholder');

    return (users || []).map((item: any) => {
      return {
        name: String(item.name),
      };
    });
  }

  return (
    <>
      {cardSections.map(
        ({ id, title, data }) =>
          data.length > 0 && (
            <Section key={id}>
              <Typography.Title level={4}>{t(title)}</Typography.Title>
              <Table id={id} dataSource={convertData(data)} columns={columns} pagination={pagination} />
            </Section>
          ),
      )}
      {digitalBusinessPaymentUsers && digitalBusinessPaymentUsers?.length > 0 && (
        <Section>
          <Typography.Title level={4}>{t('Digital Business Payments')}</Typography.Title>
          <Table
            id="reviewAndSubmitDigitalBusinessUsersTable"
            dataSource={getVirtualCardUsers(digitalBusinessPaymentUsers)}
            columns={columnsPortalUsers}
            pagination={pagination}
          />
        </Section>
      )}
      {digitalTravelPaymentUsers && digitalTravelPaymentUsers?.length > 0 && (
        <Section>
          <Typography.Title level={4}>{t('Digital Travel Payments')}</Typography.Title>
          <Table
            id="reviewAndSubmitDigitalTravelUsersTable"
            dataSource={getVirtualCardUsers(digitalTravelPaymentUsers)}
            columns={columnsPortalUsers}
            pagination={pagination}
          />
        </Section>
      )}
    </>
  );
};

const getCardSections = (data: any[]): CardSection[] =>
  cardRoleSections.map(({ role, id, title }) => ({
    id,
    title,
    data: data.filter((item: any) => item.userRoles?.includes(role)),
  }));

const convertData = (data: any | undefined): RowDataType[] | undefined => {
  return data?.map((item: any) => {
    return {
      fullName: String(item.firstName) + String(item.lastName),
      designtation: item?.designation,
      email: item?.email,
      phoneNumber: item?.mobile,
      consent:
        item?.status === 'RECEIVED_CONSENT' ? (
          <CheckOutlined style={{ color: colors.blue[1100] }} twoToneColor="#ff0000" />
        ) : (
          <></>
        ),
    };
  });
};

export default CardholdersTab;
